export enum LeaveStatus {
  DRAFT = "DRAFT",
  PENDING = "PENDING",
  APPROVED = "APPROVED",
  REJECTED = "REJECTED",
  CANCELLED = "CANCELLED",
}

export interface ApiResponse<T> {
  responseBody: {
    statusCode: number;
    message: string;
    data: T;
  };
}

export enum Role {
  ADMIN = "ADMIN",
  HR = "HR",
  MANAGER = "MANAGER",
  EMPLOYEE = "EMPLOYEE",
}

export enum EmployeeStatus {
  ACTIVE = "ACTIVE",
  INACTIVE = "INACTIVE",
}

export type UserRole = "employee" | "manager" | "hr" | "admin";

export enum LeaveTypeEnum {
  SICK_LEAVE = "SICK_LEAVE",
  CASUAL_LEAVE = "CASUAL_LEAVE",
  PAID_LEAVE = "PAID_LEAVE",
  COMP_OFF = "COMP_OFF",
  UNPAID_LEAVE = "UNPAID_LEAVE",
  MATERNITY_LEAVE = "MATERNITY_LEAVE",
  PATERNITY_LEAVE = "PATERNITY_LEAVE",
  BEREAVEMENT_LEAVE = "BEREAVEMENT_LEAVE",
  STUDY_LEAVE = "STUDY_LEAVE",
  OTHER = "OTHER",
}

export enum Gender {
  MALE = "MALE",
  FEMALE = "FEMALE",
  NOT_SPECIFIED = "NOT_SPECIFIED",
}

export enum CountryEnum {
  IN = "IN",
  US = "US",
  UK = "UK",
}

export interface Team {
  id: string;
  name: string;
  description?: string;
  createdAt?: string;
}

export interface LeaveQuota {
  year: number;
  quotas: Record<string, number>;
}

export interface User {
  id: string;
  name: string;
  email: string;
  authProvider?: "email" | "google";
  roles: UserRole[];
  managerId?: string;
  gender?: Gender;
  country?: CountryEnum;
  firstName?: string;
  lastName?: string;
  contactNumber?: string;
  address?: string;
  dob?: string;
  emergencyContact?: string;
  isProfileCompleted?: boolean;
}

export interface Employee {
  id: string;
  employeeId: string;
  firstName: string;
  lastName: string;
  contactNumber: string;
  email: string;
  department?: string | null;
  managerId?: string | null;
  joinedDate: string;
  role: Role;
  jobTitle: string | null;
  teamId: string;
  country: CountryEnum;
  team?: {
    id: string;
    name: string;
  };
  status: EmployeeStatus;
  gender?: Gender;
  address?: string | null;
  dob?: string | null;
  emergencyContact?: string | null;
  isProfileCompleted?: boolean;
  name?: string;
  joiningDate?: string;
}

export interface Holiday {
  id: string;
  date: string;
  name: string;
  country: CountryEnum;
  type?: "public" | "optional";
}

export interface LeaveApplication {
  id: string;
  employeeId: string;
  employeeName?: string;
  startDate: string;
  endDate: string;
  leaveType: LeaveTypeEnum;
  totalDays?: number;
  reason: string | null;
  status: LeaveStatus;
  appliedDate?: string;
  createdAt?: string;
  updatedAt?: string;
  managerId: string;
  rejectionReason?: string;
  managerRemark?: string | null;
  employee?: Employee;
  manager?: Employee;
  attachmentUrl?: string | null;
  cancelledBy?: string | null;
  managers?: Array<{
    id: string;
    manager: Employee;
  }>;
}

export interface DashboardSummary {
  totalEmployees?: number;
  activeEmployees?: number;
  deletedEmployees?: number;
  totalHolidays?: number;
  totalLeaveRequests?: number;
  pendingApprovals?: number;
  approvedLeaves?: number;
  rejectedLeaves?: number;

  upcomingHolidays: Holiday[];
  teamOnLeaveToday: Array<{
    employeeId: string;
    name: string;
    teamName: string;
    leaveType: string;
    startDate: string;
    endDate: string;
  }>;

  userTeamName?: string;

  recentLeaveRequests?: Array<{
    id: string;
    employeeName: string;
    type: string;
    status: LeaveStatus;
    createdAt: string;
  }>;

  myLeaves?: {
    total: number;
    pending: number;
    approved: number;
    rejected: number;
    available?: number;
  };
  myUpcomingLeaves?: Array<{
    id: string;
    type: string;
    startDate: string;
    endDate: string;
    status: LeaveStatus;
  }>;
  leaveBalances?: Array<{
    leaveType: string;
    year: number;
    allocated: number;
    used: number;
    remaining: number;
  }>;
}

export interface LoginResponse {
  user: User;
  token: string;
}

export interface LeaveSummaryReport {
  totalRequests: number;
  approved: number;
  rejected: number;
  pending: number;
  cancelled: number;
  byType: Array<{
    leaveType: string;
    count: number;
  }>;
}

export interface EmployeeLeaveUsageReport {
  employeeId: string;
  name: string;
  email: string;
  team: string; // Team name
  leaveUsage: Array<{
    leaveType: string;
    allocated: number;
    used: number;
    remaining: number;
  }>;
}

export interface MonthlyTrendReport {
  month: string; // e.g., "January" or "2024-01"
  totalRequests: number;
  approved: number;
  rejected: number;
  pending: number;
}

export interface TeamLeaveUsageReport {
  teamId: string;
  teamName: string;
  totalEmployees: number;
  totalLeavesTaken: number;
  byType: Array<{
    leaveType: string;
    count: number;
  }>;
}
